import type { ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { EditableText, Flex, Icon, Text } from '@nula/core-ui';
import { RecordingDot } from '@/components/recording-dot/recording-dot';
import type { SurfaceEntry } from '@/lib/sync/document-surfaces';
import { surfaceMeta } from '@/lib/sync/surface-meta';
import styles from './surface-row.module.css';

interface SurfaceRowProps {
  entry: SurfaceEntry;
  active: boolean;
  onSelect: (id: string) => void;
  /** Rendered as a sibling after the selectable control — e.g. the row menu (Task 3). */
  trailing?: ReactNode;
  /** True while this row's name is being renamed inline (Task 6 owns which row). */
  editing?: boolean;
  onRenameCommit?: (title: string) => void;
  onRenameCancel?: () => void;
  className?: string;
  /**
   * True while this surface is being recorded into (live client state from
   * useLiveRecording, not a manifest status). Swaps the status dot for the
   * shared RecordingDot and suppresses the trailing menu — nothing to rename
   * or move mid-capture (ui-design §5.2).
   */
  live?: boolean;
  /** Roving-focus tab stop: `0` for the single reachable row, `-1` for the rest. */
  tabIndex?: number;
}

const STATUS_LABEL: Record<string, string> = {
  generating: 'Generating',
  streaming: 'Generating',
  failed: 'Failed',
  stale: 'Stale',
};

const STATUS_CLASS: Record<string, string | undefined> = {
  generating: styles.statusGenerating,
  streaming: styles.statusGenerating,
  failed: styles.statusFailed,
  stale: styles.statusStale,
};

const row = cva(styles.row, {
  variants: {
    active: { true: styles.active },
  },
});

/**
 * One row in the surfaces panel: rail dot, icon, name, kind label, status dot,
 * and an optional trailing control (the row menu, Task 3).
 *
 * The row is a plain `div` container (not a core-ui `Flex`: this repo's
 * vitest config sets `classNameStrategy: 'non-scoped'`, which collapses
 * `Tooltip.module.css`'s `.base { pointer-events: none }` and
 * `Flex.module.css`'s `.base { display: flex }` into one selector — a
 * test-harness collision, not a browser one, since class names are hashed in
 * a real build. It broke both the selectable control and the trailing
 * control here under jsdom — see the M5 precedents documented in
 * surface-mount.test.tsx / surface-panel.test.tsx).
 * The selectable control is the inner `menuitemradio` button. A `⌄` menu
 * trigger cannot nest inside that button — a button inside a button is
 * invalid HTML — so it renders as a sibling instead. Hover/active styling
 * stays on the container so hovering anywhere in the row (including over the
 * trailing control) highlights it. `className` still targets the selectable
 * control, matching this component's existing contract.
 *
 * While `editing`, the name is lifted out of the `menuitemradio` button
 * entirely: an `<input>` inside a `<button>` has the same invalid-nesting
 * problem the trailing slot does, and `EditableText`'s field would also
 * swallow the row's click-to-select behaviour. The rest of the row (rail,
 * icon, kind, status) still renders inside the selectable control so clicking
 * them still selects the surface; only the name swaps position.
 */
export function SurfaceRow({
  entry, active, onSelect, trailing, editing = false, onRenameCommit, onRenameCancel, className,
  live = false, tabIndex = -1,
}: SurfaceRowProps) {
  const meta = surfaceMeta(entry);
  const statusClass = STATUS_CLASS[entry.status];
  const trailingSlot = live ? undefined : trailing;

  if (editing) {
    return (
      <div
        className={row({ active })}
        data-surface-id={entry.id}
        onKeyDown={(e) => {
          // EditableText handles Escape itself (cancels the rename) but does
          // not stop it bubbling — left alone, the same keydown reaches the
          // panel's FocusTrap and closes the whole popover instead of just
          // exiting rename mode.
          if (e.key === 'Escape') e.stopPropagation();
        }}
      >
        <Flex component="span" className={clsx(styles.rail, active && styles.railActive)} />
        <Icon name={meta.icon} className={styles.icon} size={18} />
        <EditableText
          value={entry.title}
          editing
          size="sm"
          className={clsx(styles.name, className)}
          onChange={(title) => onRenameCommit?.(title)}
          onCancel={onRenameCancel}
        />
        {trailingSlot}
      </div>
    );
  }

  return (
    <div className={row({ active })} data-surface-id={entry.id}>
      <button
        type="button"
        role="menuitemradio"
        aria-checked={active}
        tabIndex={tabIndex}
        onClick={() => onSelect(entry.id)}
        className={clsx(styles.control, className)}
      >
        <Flex component="span" className={clsx(styles.rail, active && styles.railActive)} />
        {/* The icon carries the kind as its accessible name. It used to be shown
            as text beside the title too, which was redundant visually — but the
            Icon is aria-hidden by default, so dropping the text without moving
            the label here would have made the kind invisible to a screen
            reader. */}
        <Icon name={meta.icon} aria-label={meta.label} className={styles.icon} size={18} />
        <Text className={styles.name}>{entry.title}</Text>
        {live ? (
          <RecordingDot />
        ) : (
          statusClass && (
            <Flex component="span" align="center" className={styles.statusGroup}>
              <Flex
                component="span"
                data-testid="surface-status"
                role="img"
                aria-label={STATUS_LABEL[entry.status]}
                className={clsx(styles.status, statusClass)}
              />
              {/* Hidden from assistive tech: the dot already names the status,
                  and the row's name would otherwise say it twice. */}
              <Text size="xs" type="secondary" aria-hidden="true">{STATUS_LABEL[entry.status]}</Text>
            </Flex>
          )
        )}
      </button>
      {trailingSlot}
    </div>
  );
}
